"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { Check, X, Sparkles, ArrowRight, PartyPopper } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { phCapture } from "@/utils/analytics";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import {
  useChecklistState,
  CELEBRATION_TEXT,
  type ChecklistItem,
} from "@/components/onboarding/use-checklist-state";
import { ToolNavigationLink } from "@/components/shell/ToolNavigationLink";

const PLAN_TITLES: Record<string, string> = {
  "ev-plan": "Getting Started with +EV",
  "whale-watch": "Getting Started with Whale Watch",
};

interface MobileOnboardingChecklistProps {
  hasOnboardingPreferences: boolean;
  checklistDismissed: boolean;
}

export function MobileOnboardingChecklist({
  hasOnboardingPreferences,
  checklistDismissed,
}: MobileOnboardingChecklistProps) {
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pillVisible, setPillVisible] = useState(false);
  const celebrationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    planType,
    items,
    completedCount,
    totalCount,
    progressPercent,
    allComplete,
    isItemComplete,
    shouldShow,
    isAnimatingOut,
    isCelebrating,
    handleDismiss,
    handleItemClick,
    handleTransitionEnd,
  } = useChecklistState({
    hasOnboardingPreferences,
    checklistDismissed,
  });

  useEffect(() => {
    if (!shouldShow || !isMobile) return;
    const timer = setTimeout(() => setPillVisible(true), 300);
    return () => clearTimeout(timer);
  }, [shouldShow, isMobile]);

  useEffect(() => {
    if (!allComplete || !shouldShow) return;

    celebrationTimerRef.current = setTimeout(() => {
      setDrawerOpen(false);
    }, 2500);

    return () => {
      if (celebrationTimerRef.current) {
        clearTimeout(celebrationTimerRef.current);
        celebrationTimerRef.current = null;
      }
    };
  }, [allComplete, shouldShow]);

  const handlePillTap = useCallback(() => {
    setDrawerOpen(true);
    phCapture("checklist_mobile_pill_tapped", {
      plan_type: planType,
      items_completed: completedCount,
      total_items: totalCount,
      device: "mobile",
    });
  }, [planType, completedCount, totalCount]);

  const handleDrawerItemClick = useCallback(
    (item: ChecklistItem) => {
      handleItemClick(item);
      setDrawerOpen(false);
    },
    [handleItemClick],
  );

  const handleDrawerDismiss = useCallback(() => {
    setDrawerOpen(false);
    handleDismiss();
  }, [handleDismiss]);

  if (!isMobile || !shouldShow) return null;

  const celebrationText =
    planType && CELEBRATION_TEXT[planType]
      ? CELEBRATION_TEXT[planType]
      : "You're ready to compare the board.";

  const planTitle =
    planType && PLAN_TITLES[planType]
      ? PLAN_TITLES[planType]
      : "Getting Started";

  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  return (
    <>
      <button
        type="button"
        onClick={handlePillTap}
        className={cn(
          "fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom)+0.75rem)] right-4 z-30 md:hidden",
          "flex items-center gap-2 px-3 py-2.5 rounded-full",
          "border border-white/10 bg-surface-raised",
          "shadow-[0_4px_20px_rgba(0,0,0,0.5)]",
          "transition-[opacity,transform] duration-500 ease-out",
          pillVisible && !isAnimatingOut
            ? "opacity-100 scale-100"
            : "opacity-0 scale-75 pointer-events-none",
        )}
        onTransitionEnd={handleTransitionEnd}
        aria-label={`Onboarding checklist: ${completedCount} of ${totalCount} complete`}
      >
        <div className="relative size-8 shrink-0">
          <svg
            className="size-8 -rotate-90"
            viewBox="0 0 36 36"
            aria-hidden="true"
          >
            <circle
              cx="18"
              cy="18"
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="3"
            />
            <circle
              cx="18"
              cy="18"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="text-primary transition-all duration-500 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {allComplete ? (
              <Check className="size-3.5 text-success" />
            ) : (
              <Sparkles className="size-3 text-primary" />
            )}
          </div>
        </div>

        <span className="text-xs font-semibold text-foreground/80 tabular-nums pr-0.5">
          {completedCount}/{totalCount}
        </span>
      </button>

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="md:hidden">
          <DrawerHeader>
            <div className="flex-1">
              <DrawerTitle>{planTitle}</DrawerTitle>
              <DrawerDescription className="sr-only">
                Track your onboarding progress
              </DrawerDescription>
            </div>
            <DrawerClose asChild>
              <button
                type="button"
                className="min-h-[44px] min-w-[44px] rounded-full flex items-center justify-center hover:bg-white/5 transition-colors"
                aria-label="Close"
              >
                <X className="size-4 text-muted-foreground" />
              </button>
            </DrawerClose>
          </DrawerHeader>

          <div className="px-4 pt-3 pb-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-muted-foreground">
                {completedCount} of {totalCount} complete
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {progressPercent}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {!isCelebrating && (
            <div className="px-4 pt-3 pb-2 space-y-1">
              {items.map((item) => {
                const isDone = isItemComplete(item.id);
                const content = (
                  <div
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-3 min-h-[44px] transition-colors",
                      item.href && !isDone
                        ? "hover:bg-white/[0.04] active:bg-white/[0.06] cursor-pointer"
                        : "",
                    )}
                  >
                    <div
                      className={cn(
                        "h-6 w-6 rounded-full shrink-0 flex items-center justify-center border-2 transition-colors",
                        isDone
                          ? "bg-success/80 border-success/60"
                          : "border-white/20 bg-transparent",
                      )}
                    >
                      {isDone && <Check className="size-3.5 text-background" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          "text-sm font-medium",
                          isDone
                            ? "text-muted-foreground/50 line-through"
                            : "text-foreground/90",
                        )}
                      >
                        {item.label}
                      </p>
                      <p
                        className={cn(
                          "text-xs mt-0.5",
                          isDone
                            ? "text-muted-foreground/30"
                            : "text-muted-foreground/60",
                        )}
                      >
                        {item.description}
                      </p>
                    </div>

                    {item.href && !isDone && (
                      <ArrowRight className="size-4 text-muted-foreground/30 shrink-0" />
                    )}
                  </div>
                );

                if (item.href && !isDone) {
                  return (
                    <ToolNavigationLink
                      key={item.id}
                      href={item.href}
                      onClick={() => handleDrawerItemClick(item)}
                    >
                      {content}
                    </ToolNavigationLink>
                  );
                }

                return <div key={item.id}>{content}</div>;
              })}
            </div>
          )}

          {isCelebrating && (
            <div className="px-4 py-8 flex flex-col items-center gap-3">
              <PartyPopper className="size-10 text-accent-value" />
              <p className="text-base font-semibold text-foreground">
                All done!
              </p>
              <p className="text-sm text-accent-value text-center">
                {celebrationText}
              </p>
            </div>
          )}

          {!allComplete && (
            <DrawerFooter>
              <button
                type="button"
                onClick={handleDrawerDismiss}
                className="flex items-center gap-1.5 text-xs text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors"
              >
                <X className="size-3" />
                Dismiss checklist
              </button>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}
